import {
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, Button, Checkbox, Flex, Text, VStack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** The subset of the backend migration preview this dialog puts on screen. */
export interface ApplyFormatToExistingCodesPreview {
  renamed: number;
  kept: number;
  transitionDays: number;
  running: { migrationId: string } | null;
}

export interface ApplyFormatToExistingCodesDialogProps {
  open: boolean;
  preview: ApplyFormatToExistingCodesPreview;
  onConfirm: (applyToExisting: boolean) => void;
  onCancel: () => void;
}

/**
 * Asks whether the format the brand just chose should also be applied to the
 * codes already handed to affiliates. Pure: it decides nothing and calls
 * nothing, it answers `onConfirm(applyToExisting)`. The consequences of the
 * migration only appear once the box is ticked, so the ordinary save stays a
 * single sentence. The box and those consequences sit in a warning alert: what
 * is at stake is the codes affiliates already hold, not the format itself,
 * which is why the plain description stays outside it. A migration already
 * running replaces the box entirely:
 * there is nothing to offer while one is in flight, and confirming then only
 * saves the format.
 */
const ApplyFormatToExistingCodesDialog = ({
  open,
  preview,
  onConfirm,
  onCancel,
}: ApplyFormatToExistingCodesDialogProps) => {
  const { t } = useTranslation();
  const [applyToExisting, setApplyToExisting] = useState(false);
  const isRunning = preview.running !== null;

  useEffect(() => {
    if (open) setApplyToExisting(false);
  }, [open]);

  return (
    <DialogRoot open={open} onOpenChange={(event) => !event.open && onCancel()}>
      <DialogContent data-testid="apply-format-to-existing-codes-dialog">
        <DialogHeader>
          <DialogTitle>{t('programs.modals.editDiscountCode.applyToExisting.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack align="stretch" gap={3}>
            <DialogDescription fontSize="sm" color="fg.muted">
              {t('programs.modals.editDiscountCode.applyToExisting.text')}
            </DialogDescription>

            {isRunning ? (
              <Text fontSize="sm" color="fg.muted">
                {t('programs.modals.editDiscountCode.applyToExisting.running')}
              </Text>
            ) : (
              <Alert.Root status="warning" data-testid="apply-format-to-existing-codes-warning">
                <Alert.Indicator />
                <VStack align="stretch" gap={3}>
                  <Checkbox.Root
                    checked={applyToExisting}
                    onCheckedChange={({ checked }) => setApplyToExisting(checked === true)}
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                    <Checkbox.Label>
                      {t('programs.modals.editDiscountCode.applyToExisting.checkbox')}
                    </Checkbox.Label>
                  </Checkbox.Root>

                  {applyToExisting && (
                    <VStack align="stretch" gap={2} pl={7}>
                      <Text fontSize="sm">
                        {t('programs.modals.editDiscountCode.applyToExisting.countsRenamed', {
                          count: preview.renamed,
                        })}
                      </Text>
                      <Text fontSize="sm">
                        {t('programs.modals.editDiscountCode.applyToExisting.countsKept', {
                          count: preview.kept,
                        })}
                      </Text>
                      <Text fontSize="sm">
                        {t('programs.modals.editDiscountCode.applyToExisting.window', {
                          days: preview.transitionDays,
                        })}
                      </Text>
                      <Text fontSize="sm">
                        {t('programs.modals.editDiscountCode.applyToExisting.relay')}
                      </Text>
                    </VStack>
                  )}
                </VStack>
              </Alert.Root>
            )}
          </VStack>
        </DialogBody>
        <DialogFooter>
          <Flex w="full" justify="flex-end" gap={2} wrap="wrap">
            <Button variant="outline" onClick={onCancel}>
              {t('programs.modals.editDiscountCode.applyToExisting.cancel')}
            </Button>
            <Button onClick={() => onConfirm(!isRunning && applyToExisting)}>
              {t('programs.modals.editDiscountCode.applyToExisting.confirm')}
            </Button>
          </Flex>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default ApplyFormatToExistingCodesDialog;
